/* lystia-overlay.js — оверлеї sheet / drawer / modal / fullscreen (T-E2-11, parity/overlays.md §2-§6).
   Підключення: <script src="{{ 'lystia-overlay.js' | asset_url }}" defer> ПІСЛЯ lystia-core.js. Розмітка: snippets/overlay.liquid -> <lystia-overlay>.
   API: document.lystiaOverlay { open(name, props) · close() · refresh(props) · current() · isOpen(name) · has(name) · panel() }
        <lystia-overlay>.setBody(node|html) · .setFooter(node|html|null)
   Декларативно: [data-overlay-open="name"] / [data-overlay-close] (делегування кліку).
   Події (core): lystia:overlay-open {name, props, panel, element} · lystia:overlay-close {name}. */
(function () {
  'use strict';
  const core = document.lystia;
  if (!core) { console.error('[lystia-overlay] потрібен lystia-core.js (document.lystia)'); return; }
  if (document.lystiaOverlay) return;

  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';
  const mqHover = window.matchMedia('(hover: hover)');
  const registry = new Map(); /* name -> <lystia-overlay> */
  /* Єдиний оверлей: closed -> opening -> open -> closing -> closed */
  const OV = { state: 'closed', name: null, props: null, host: null, el: null, panel: null, body: null, foot: null, pushed: false, ignorePop: 0, opener: null, timer: 0 };

  const $$ = (sel, root) => Array.prototype.slice.call((root || document).querySelectorAll(sel));
  const mk = (tag, cls, attrs) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (attrs) Object.keys(attrs).forEach((k) => n.setAttribute(k, attrs[k]));
    return n;
  };
  /* рядок HTML -> DocumentFragment через інертний <template> (скрипти не виконуються) */
  function toFragment(c) {
    if (c == null) return document.createDocumentFragment();
    if (c instanceof DocumentFragment) return c;
    if (c instanceof Node) { const f = document.createDocumentFragment(); f.appendChild(c); return f; }
    const t = document.createElement('template');
    t.innerHTML = String(c);
    return t.content;
  }

  class LystiaOverlay extends HTMLElement {
    connectedCallback() { if (this.getAttribute('name')) registry.set(this.getAttribute('name'), this); }
    disconnectedCallback() {
      const n = this.getAttribute('name');
      if (registry.get(n) === this) registry.delete(n);
    }
    slotTemplate(s) { return this.querySelector(':scope > template[data-slot="' + s + '"]'); }
    /** Режим за пристроєм: desktop ? (desktop-mode || (sheet -> modal)) : mode. */
    modeFor() {
      const mode = this.getAttribute('mode') || 'sheet';
      if (core.device() !== 'desktop') return mode;
      return this.getAttribute('desktop-mode') || (mode === 'sheet' ? 'modal' : mode);
    }
    /** Замінити тіло (і для наступних відкриттів). */
    setBody(c) {
      const t = this.slotTemplate('body');
      if (t) t.content.replaceChildren(toFragment(c).cloneNode(true));
      if (OV.host === this && OV.body) OV.body.replaceChildren(toFragment(c));
    }
    setFooter(c) {
      let t = this.slotTemplate('footer');
      if (c == null) { if (t) t.content.replaceChildren(); } else {
        if (!t) { t = document.createElement('template'); t.setAttribute('data-slot', 'footer'); this.appendChild(t); }
        t.content.replaceChildren(toFragment(c).cloneNode(true));
      }
      if (OV.host === this && OV.foot) fillFoot();
    }
  }
  if (!customElements.get('lystia-overlay')) customElements.define('lystia-overlay', LystiaOverlay);

  /* ---- Побудова DOM ---- */
  function closeIcon(host) {
    const t = host.slotTemplate('close-icon');
    return t ? t.content.cloneNode(true) : document.createTextNode('×');
  }
  function fillBody() {
    const t = OV.host.slotTemplate('body');
    OV.body.replaceChildren(t ? t.content.cloneNode(true) : document.createDocumentFragment());
  }
  function fillFoot() {
    const t = OV.host.slotTemplate('footer');
    const has = !!(t && t.content.childNodes.length);
    OV.foot.replaceChildren(has ? t.content.cloneNode(true) : document.createDocumentFragment());
    OV.foot.hidden = !has;
  }
  function build() {
    const host = OV.host;
    const mode = host.modeFor();
    const heading = host.getAttribute('heading');
    const noHead = host.hasAttribute('no-head') || !heading;
    const closeLabel = host.getAttribute('close-label') || '';
    const root = document.getElementById('overlay-root') || document.body;
    const el = mk('div', ['ov', 'ov--' + mode, 'ov-name--' + OV.name, host.getAttribute('class-name') || ''].join(' ').trim(), {
      role: 'dialog', 'aria-modal': 'true', 'aria-label': heading || host.getAttribute('label') || OV.name, tabindex: '-1',
    });
    const scrim = mk('div', 'ov__scrim');
    scrim.addEventListener('click', close);
    const panel = mk('div', 'ov__panel');
    if (host.getAttribute('width')) panel.style.setProperty('--ov-w', host.getAttribute('width') + 'px');
    if (mode === 'sheet') { const hd = mk('div', 'ov__handle', { 'aria-hidden': 'true' }); hd.appendChild(mk('span')); panel.appendChild(hd); }
    const closeBtn = (cls) => {
      const b = mk('button', cls, { type: 'button', 'aria-label': closeLabel });
      b.appendChild(closeIcon(host));
      b.addEventListener('click', close);
      return b;
    };
    if (!noHead) {
      const head = mk('div', 'ov__head');
      const h2 = mk('h2', 'ov__title'); h2.textContent = heading;
      head.append(h2, closeBtn('ov__close'));
      panel.appendChild(head);
    } else {
      panel.appendChild(closeBtn('ov__close ov__close--float'));
    }
    const body = mk('div', 'ov__body');
    const foot = mk('div', 'ov__foot');
    panel.append(body, foot);
    el.append(scrim, panel);
    root.appendChild(el);
    OV.el = el; OV.panel = panel; OV.body = body; OV.foot = foot;
    fillBody(); fillFoot();
    el.addEventListener('keydown', onKey);
    el.addEventListener('click', onLinkClick);
    if (mode === 'sheet' || (mode === 'fullscreen' && host.hasAttribute('swipe-close'))) enableDrag(panel); /* A-106: свайп для меню M */
  }

  /* ---- Відкриття / закриття ---- */
  function open(name, props) {
    const host = registry.get(name);
    if (!host) { console.warn('[lystia-overlay] не знайдено: ' + name); return; }
    const replacing = OV.state === 'open' || OV.state === 'opening' || OV.state === 'closing';
    if (replacing) teardown(true);
    OV.opener = replacing ? OV.opener : document.activeElement;
    OV.name = name; OV.props = props || {}; OV.host = host; OV.state = 'opening';
    build();
    if (!replacing) core.scrollLock.lock();
    if (OV.pushed) history.replaceState({ overlay: name }, '', location.href);
    else { history.pushState({ overlay: name }, '', location.href); OV.pushed = true; }
    document.body.classList.add('has-overlay');
    document.body.dataset.overlay = name;
    void OV.panel.offsetHeight; /* примусовий reflow (не rAF) */
    OV.el.classList.add('is-open');
    OV.state = 'open';
    setTimeout(focusFirst, 30);
    core.emit('overlay-open', { name: name, props: OV.props, panel: OV.panel, element: host });
  }
  function close() {
    if (OV.state !== 'open' && OV.state !== 'opening') return;
    animateClose();
    if (OV.pushed) { OV.pushed = false; OV.ignorePop++; history.back(); }
  }
  function animateClose() {
    if (!OV.el) return;
    OV.state = 'closing';
    const el = OV.el;
    el.classList.remove('is-open');
    el.classList.add('is-closing');
    clearTimeout(OV.timer);
    OV.timer = setTimeout(() => { if (OV.el === el) teardown(false); }, core.reducedMotion() ? 0 : 300);
  }
  function teardown(replacing) {
    clearTimeout(OV.timer);
    const name = OV.name;
    if (OV.el) { OV.el.removeEventListener('keydown', onKey); OV.el.remove(); }
    OV.el = OV.panel = OV.body = OV.foot = null;
    if (!replacing) {
      OV.state = 'closed'; OV.name = null; OV.props = null; OV.host = null;
      core.scrollLock.unlock();
      document.body.classList.remove('has-overlay');
      delete document.body.dataset.overlay;
      try { if (OV.opener && OV.opener.focus) OV.opener.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
      OV.opener = null;
      core.emit('overlay-close', { name: name });
    }
  }
  function refresh(props) {
    if (!OV.el) return;
    if (props) OV.props = Object.assign({}, OV.props, props);
    fillBody(); fillFoot();
  }
  function closeSilently() { if (OV.el) teardown(false); OV.pushed = false; }

  /* ---- Back / popstate ---- */
  window.addEventListener('popstate', () => {
    if (OV.ignorePop > 0) { OV.ignorePop--; return; }
    if (OV.el && (OV.state === 'open' || OV.state === 'opening')) { OV.pushed = false; animateClose(); }
  });

  /* ---- Навігація з оверлея: закрити без анімації, запис історії оверлея підміняється маршрутом ---- */
  function onLinkClick(e) {
    const a = e.target.closest && e.target.closest('a[href]');
    if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if ((a.target && a.target !== '_self') || a.hasAttribute('download') || a.hasAttribute('data-no-close')) return;
    let url;
    try { url = new URL(a.href, location.href); } catch (er) { return; }
    if (url.origin !== location.origin) return;
    e.preventDefault();
    const wasPushed = OV.pushed;
    closeSilently();
    if (!wasPushed) { location.href = url.href; return; }
    let done = false;
    const go = () => { if (done) return; done = true; window.removeEventListener('popstate', go); location.href = url.href; };
    OV.ignorePop++;
    window.addEventListener('popstate', go);
    setTimeout(go, 400);
    history.back();
  }

  /* ---- Фокус, клавіатура ---- */
  function focusFirst() {
    if (!OV.panel) return;
    const closeBtn = OV.panel.querySelector('.ov__close');
    const f = $$(FOCUSABLE, OV.body).filter((x) => x.offsetParent !== null)[0] || closeBtn;
    const target = f && f.tagName === 'INPUT' && !mqHover.matches ? closeBtn : f;
    try { (target || OV.el).focus({ preventScroll: true }); } catch (e) { /* ignore */ }
  }
  function onKey(e) {
    if (e.key === 'Escape') { e.stopPropagation(); close(); return; }
    if (e.key !== 'Tab' || !OV.panel) return;
    const items = $$(FOCUSABLE, OV.panel).filter((x) => x.offsetParent !== null);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ---- Drag-вниз (лише sheet): ручка/шапка завжди; тіло — коли scrollTop=0 ---- */
  function enableDrag(panel) {
    let startY = 0, dy = 0, t0 = 0, dragging = false, fromBody = false, pid = null;
    const body = panel.querySelector('.ov__body');
    const onDown = (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      const inHead = e.target.closest('.ov__handle, .ov__head');
      fromBody = !inHead && body.contains(e.target);
      if (!inHead && !fromBody) return;
      if (inHead && e.target.closest('button, a')) return;
      if (e.target.closest('input, textarea, select, [data-no-drag]')) return;
      if (fromBody && body.scrollTop > 0) return;
      startY = e.clientY; dy = 0; t0 = performance.now(); dragging = false; pid = e.pointerId;
      panel.addEventListener('pointermove', onMove);
      panel.addEventListener('pointerup', onUp);
      panel.addEventListener('pointercancel', onUp);
    };
    const onMove = (e) => {
      if (e.pointerId !== pid) return;
      const d = e.clientY - startY;
      if (!dragging) {
        if (d > 6 && (!fromBody || body.scrollTop <= 0)) {
          dragging = true; panel.classList.add('is-dragging');
          try { panel.setPointerCapture(pid); } catch (er) { /* ignore */ }
        } else if (d < -6) { cleanup(); return; } else return;
      }
      dy = Math.max(0, d);
      panel.style.transform = 'translateY(' + dy + 'px)';
      if (OV.el) OV.el.style.setProperty('--ov-drag', String(Math.max(0, 1 - dy / panel.offsetHeight)));
      e.preventDefault();
    };
    const onUp = () => {
      const v = dy / Math.max(1, performance.now() - t0);
      const was = dragging;
      cleanup();
      if (!was) return;
      panel.classList.remove('is-dragging');
      if (dy > Math.min(140, panel.offsetHeight * 0.3) || v > 0.6) { panel.style.transform = ''; close(); }
      else { panel.style.transform = ''; if (OV.el) OV.el.style.removeProperty('--ov-drag'); }
    };
    const cleanup = () => {
      panel.removeEventListener('pointermove', onMove);
      panel.removeEventListener('pointerup', onUp);
      panel.removeEventListener('pointercancel', onUp);
      try { panel.releasePointerCapture(pid); } catch (er) { /* ignore */ }
      dragging = false;
    };
    panel.addEventListener('pointerdown', onDown);
    panel.addEventListener('touchmove', (e) => { if (dragging) e.preventDefault(); }, { passive: false });
  }

  /* ---- Зміна брейкпоінта при відкритому: перебудова в новому режимі без анімації ---- */
  core.on('device-change', () => {
    if (OV.el && OV.state === 'open') { teardown(true); build(); OV.el.classList.add('is-open'); }
  });

  /* ---- Декларативні тригери ---- */
  document.addEventListener('click', (e) => {
    const t = e.target.closest && e.target.closest('[data-overlay-open],[data-overlay-close]');
    if (!t) return;
    if (t.hasAttribute('data-overlay-close')) { close(); return; }
    e.preventDefault();
    open(t.getAttribute('data-overlay-open'));
  });

  document.lystiaOverlay = Object.freeze({
    open: open,
    close: close,
    refresh: refresh,
    closeSilently: closeSilently,
    current: () => (OV.state === 'open' || OV.state === 'opening' ? OV.name : null),
    isOpen: (name) => (OV.state === 'open' || OV.state === 'opening') && OV.name === name,
    has: (name) => registry.has(name),
    panel: () => OV.panel,
  });
})();
